import React, { useContext, useState } from 'react'
import _ from 'lodash'
import { Trans, t } from '@lingui/macro'
import { ChartType } from 'chart.js'
import { TimeUnitEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconDocEnrolment from '../../../components/common/icon/IconDocEnrolment'
import { AnalyticsHighlight } from '../AnalyticsHighlight'
import Loader from '../../../components/common/loader/Loader'
import { AnalyticsHeader } from '../AnalyticsHeader'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { TableExtendedHeader } from '../../../components/common/table-extended-old/table-extended-header/TableExtendedHeader'
import AnalyticsHourChart from '../components/AnalyticsHourChart'
import { graphOptions, hourlyGraphOptions } from '../chartOptions'
import AnalyticsChart from '../components/AnalyticsChart'
import IconCalendar from '../../../components/common/icon/IconCalendar'
import IconMap from '../../../components/common/icon/IconMap'
import IconGraph from '../../../components/common/icon/IconGraph'
import PathwaysOverviewTable from '../pathways/PathwaysOverviewTable'
import WorkbooksOverviewTable from '../workbooks/WorkbooksOverviewTable'
import UserActivityOverviewTable from '../user-activity-table/UserActivityOverviewTable'
import { EventsOverviewTable } from '../events/EventsOverviewTable'
import ErrorMessage from '../../../components/common/error/ErrorMessage'
import IconBubbles from '../../../components/common/icon/IconBubbles'
import { SurveysOverviewTable } from '../surveys/SurveysOverviewTable'
import IconBadge from '../../../components/common/icon/IconBadge'
import CertificationDetailOverviewTable from '../certification-detail/CertificationDetailOverviewTable'
import { useUserDetailAnalyticsQuery } from './UserDetailAnalytics.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { AnalyticsFiltersEnum } from '../constants'
import TabsLegacy from 'front-end/src/components/common/tabs/TabsLegacy'

const UserDetailAnalytics = () => {
  const history = useHistory()
  const params = useParams<{ userId: string }>()
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )
  const userId = parseInt(params.userId, 10)

  const { data, loading } = useUserDetailAnalyticsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filter: _.omit(filterAsQueryVariables, 'groupIds'),
      userId,
      chartTimeUnit
    }
  })

  if (loading) {
    return <Loader />
  }
  if (!data || !data.user) {
    return (
      <ErrorMessage>
        <Trans>Could not find user with ID {userId}</Trans>
      </ErrorMessage>
    )
  }

  const { user } = data
  if (user.analytics) {
    const {
      analytics: {
        people: { totalUserSessionCount },
        events: { attendedCount: attendedEventsCount },
        workbooks: { completedCount: completedWorkbooksCount },
        pathways: { completedCount: completedPathwaysCount },
        surveys: { completedCount: completedSurveysCount },
        resources: {
          totalViewsCount,
          uniqueViewedTopicsCount,
          topicViewsByHour,
          topicViewsByDay
        }
      }
    } = user

    const tabList = [
      {
        caption: t`Activity`,
        paramName: 'activity',
        icon: <IconGraph />,
        getComponent: () => <UserActivityOverviewTable userId={userId} />
      },
      {
        caption: t`Enrolments`,
        paramName: 'workbooks',
        icon: <IconDocEnrolment />,
        getComponent: () => <WorkbooksOverviewTable userId={userId} />
      },
      {
        caption: t`Certificates`,
        paramName: 'certificates',
        icon: <IconBadge />,
        getComponent: () => <CertificationDetailOverviewTable userId={userId} />
      },
      {
        caption: t`Pathways`,
        paramName: 'pathways',
        icon: <IconMap />,
        getComponent: () => <PathwaysOverviewTable userId={userId} />
      },
      {
        caption: t`Surveys`,
        paramName: 'surveys',
        icon: <IconBubbles />,
        getComponent: () => <SurveysOverviewTable userId={userId} />
      },
      {
        caption: t`Events`,
        paramName: 'events',
        icon: <IconCalendar />,
        getComponent: () => <EventsOverviewTable userId={userId} />
      }
    ]
    const renderAnalyticsBoxes = () => {
      return (
        <>
          <div className="AnalyticsRow">
            <BorderedBox className="AnalyticsRow__box">
              <div className="AnalyticsHighlight__row">
                <AnalyticsHighlight
                  value={totalViewsCount}
                  icon="eye"
                  description={t`Resource views`}
                  iconColor="blue"
                />
                <AnalyticsHighlight
                  value={(totalViewsCount / uniqueViewedTopicsCount).toFixed(1)}
                  icon="check"
                  description={t`Views per resource`}
                  iconColor="green"
                />
                <AnalyticsHighlight
                  value={(totalViewsCount / totalUserSessionCount).toFixed(1)}
                  icon="reload"
                  description={t`Visits / Sessions`}
                  iconColor="purple"
                />
              </div>

              <AnalyticsChart
                width={800}
                height={160}
                config={{
                  type: 'line' as ChartType,
                  options: graphOptions
                }}
                data={[{ dataset: topicViewsByDay, color: '11,146,213' }]}
                granularity={chartTimeUnit}
                onGranularityChanged={setChartTimeUnit}
              />
            </BorderedBox>
          </div>

          <div className="AnalyticsRow">
            <BorderedBox className="AnalyticsRow__box">
              <TableExtendedHeader title={t`Completions`} />
              <div className="AnalyticsHighlight__row completions">
                <AnalyticsHighlight
                  value={completedPathwaysCount}
                  icon="map"
                  iconColor="green"
                  description={t`Pathways`}
                />

                <AnalyticsHighlight
                  value={completedWorkbooksCount}
                  icon={<IconDocEnrolment boxSize="20px" />}
                  iconColor="green"
                  description={t`Enrolments`}
                />

                <AnalyticsHighlight
                  value={attendedEventsCount}
                  icon="calendar"
                  iconColor="green"
                  description={t`Events`}
                />
                <AnalyticsHighlight
                  value={completedSurveysCount}
                  icon="bubbles"
                  iconColor="green"
                  description={t`Surveys`}
                />
              </div>
            </BorderedBox>

            <BorderedBox className="AnalyticsRow__box">
              <TableExtendedHeader title="Activity time" />
              <div className="Analytics__hourChart">
                <AnalyticsHourChart
                  width={400}
                  height={100}
                  config={{
                    type: 'roundedBar' as ChartType,
                    options: hourlyGraphOptions
                  }}
                  data={topicViewsByHour}
                />
              </div>
            </BorderedBox>
          </div>
        </>
      )
    }

    return (
      <>
        <AnalyticsHeader
          showFilterPanel
          goBack={history.goBack}
          linkHintText={t`Go to user`}
          link={linkBuilder.userDetail(user.id)}
          activeFilters={[
            AnalyticsFiltersEnum.INCLUDE_ADMINS,
            AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
            AnalyticsFiltersEnum.DATE_FILTER,
            AnalyticsFiltersEnum.WORKSPACE_FILTER
          ]}
        >
          <>
            {user.display_name}
            {user.deleted_at && <StatusArchivedGrey mx="8px" />}
          </>
        </AnalyticsHeader>

        {renderAnalyticsBoxes()}

        <div className="AnalyticsRow">
          <div className="AnalyticsRow__box AnalyticsRow__tabs">
            <TabsLegacy tabList={tabList} justifyContent="flex-start" />
          </div>
        </div>
      </>
    )
  } else {
    return null
  }
}

export default UserDetailAnalytics
